import React, { type JSX } from "react";
import { THRESHOLD_DEFAULTS, type ThresholdOverrides } from "../../../constants/thresholdDefaults";
import type { ThresholdOverridesState } from "./useThresholdOverrides";
import {
  SectionHeader,
  ToggleSwitch,
  SECTION_CLASS,
  SECTION_BODY_CLASS,
  SECTION_FOOTER_CLASS,
} from "./SettingsPrimitives";

const FIELD_CLASS =
  "mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed";

interface ThresholdSpec {
  key: keyof ThresholdOverrides;
  label: string;
  hint: string;
  min: number;
  max: number;
  step: number;
}

/** Score bounds are all 0–1 fractions. */
const SCORE_RANGE = { min: 0, max: 1, step: 0.01 };

const DEAL_THRESHOLDS: ThresholdSpec[] = [
  {
    key: "score_floor",
    label: "Score Floor",
    hint: "Min column score for mean calculation",
    ...SCORE_RANGE,
  },
  {
    key: "result_max_threshold",
    label: "Max Threshold",
    hint: "Include deal if max score exceeds this",
    ...SCORE_RANGE,
  },
  {
    key: "result_mean_threshold",
    label: "Mean Threshold",
    hint: "Include deal if mean above-floor score exceeds this",
    ...SCORE_RANGE,
  },
];

const DOCUMENT_THRESHOLDS: ThresholdSpec[] = [
  {
    key: "page_size",
    label: "Page Size",
    hint: "Results per retrieval page",
    min: 1,
    max: 200,
    step: 1,
  },
  {
    key: "similarity_threshold",
    label: "Similarity Threshold",
    hint: "Minimum chunk similarity score",
    ...SCORE_RANGE,
  },
  {
    key: "vector_similarity_weight",
    label: "Vector Weight",
    hint: "Weight of vector vs keyword similarity",
    ...SCORE_RANGE,
  },
];

/** One numeric override, labelled with the value it replaces. */
function ThresholdField({
  spec,
  value,
  disabled,
  onChange,
}: {
  spec: ThresholdSpec;
  value: number;
  disabled: boolean;
  onChange: (key: keyof ThresholdOverrides, value: string) => void;
}): JSX.Element {
  return (
    <div>
      <label htmlFor={spec.key} className="block text-xs font-medium text-gray-600">
        {spec.label} (default: {THRESHOLD_DEFAULTS[spec.key]})
      </label>
      <input
        id={spec.key}
        type="number"
        min={spec.min}
        max={spec.max}
        step={spec.step}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(spec.key, e.target.value)}
        className={FIELD_CLASS}
      />
      <p className="mt-1 text-xs text-gray-400">{spec.hint}</p>
    </div>
  );
}

/** A titled group of threshold fields. */
function ThresholdGroup({
  title,
  specs,
  state,
}: {
  title: string;
  specs: ThresholdSpec[];
  state: ThresholdOverridesState;
}): JSX.Element {
  return (
    <div className={state.enabled ? "" : "opacity-50"}>
      <h4 className="text-sm font-medium text-gray-900 mb-3">{title}</h4>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {specs.map((spec) => (
          <ThresholdField
            key={spec.key}
            spec={spec}
            value={state.overrides[spec.key]}
            disabled={!state.enabled}
            onChange={state.update}
          />
        ))}
      </div>
    </div>
  );
}

/** Whether overrides are sent at all. */
function OverridesToggleRow({ state }: { state: ThresholdOverridesState }): JSX.Element {
  return (
    <div className="flex items-center justify-between">
      <div>
        <span id="threshold-toggle-label" className="text-sm font-medium text-gray-900">
          Send Threshold Overrides
        </span>
        <p className="text-sm text-gray-500">
          {state.enabled
            ? "Overrides are active. Non-default values will be sent with each message."
            : "Overrides are disabled. MCP servers will use their default thresholds."}
        </p>
      </div>
      <ToggleSwitch
        checked={state.enabled}
        onChange={state.toggle}
        onClass="bg-blue-600"
        labelledBy="threshold-toggle-label"
      />
    </div>
  );
}

/** Per-user overrides for the MCP search thresholds. */
export default function ThresholdOverridesSection({
  state,
}: {
  state: ThresholdOverridesState;
}): JSX.Element {
  return (
    <div className={SECTION_CLASS}>
      <SectionHeader
        title="Search Threshold Overrides"
        description="Override MCP search thresholds. Non-default values are included with each message sent to the backend."
      />

      <div className={SECTION_BODY_CLASS}>
        <OverridesToggleRow state={state} />
        <ThresholdGroup title="Deal Search Thresholds" specs={DEAL_THRESHOLDS} state={state} />
        <ThresholdGroup
          title="Document Search Thresholds"
          specs={DOCUMENT_THRESHOLDS}
          state={state}
        />
      </div>

      <div className={SECTION_FOOTER_CLASS}>
        <button
          type="button"
          onClick={state.reset}
          disabled={!state.enabled}
          className={`inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-xs ${
            state.enabled
              ? "text-gray-700 bg-white hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              : "text-gray-400 bg-gray-100 cursor-not-allowed"
          }`}
        >
          Reset to Defaults
        </button>
      </div>
    </div>
  );
}
